import { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import CallLogs from "./CallLogs";
import CallView from "./CallView";
import { get_user_active_call } from "../../api/CallControlApi";
import { useZoomActiveCallPolling } from "../../hooks/useZoomActiveCallPolling";
import { parseCallEventPayload } from "../../utilities/parseCallEventPayload";
import * as Sentry from "@sentry/react";
const { logger } = Sentry;
export default function InterpreterView() {
  const { user } = useAuth();
  const [callEvent, setCallEvent] = useState("IDLE");
  const [callInfo, setCallInfo] = useState({});
  const [formSubmitted, setFormSubmitted] = useState(false);
  const isZoomEmbedded = import.meta.env.VITE_ENV_MODE !== "DEV";

  useZoomActiveCallPolling({
    enabled: isZoomEmbedded,
    callEvent,
    formSubmitted,
    setCallEvent,
    setCallInfo,
    setFormSubmitted,
  });

  const handleWSCallMissed = async () => {
    let response = await get_user_active_call();
    if (response.message == "ACTIVE") {
      setCallEvent("ANSWERED");
      setCallInfo(response.data);
    }
  };

  useEffect(() => {
    const key = import.meta.env.VITE_PUSHER_KEY;
    const host = import.meta.env.VITE_WS_HOST;
    const port = import.meta.env.VITE_WS_PORT;

    // WebSocket connection variables
    let ws;
    let pingInterval;
    let reconnectTimeout;
    let reconnectAttempts = 0;
    const maxReconnectAttempts = 15;
    const baseReconnectDelay = 1000; // Start with 1 second

    // Function to create and setup the WebSocket connection
    const setupWebSocket = () => {
      // Clear any existing reconnect timeout
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }

      ws = new WebSocket(
        `${host}:${port}/app/${key}?protocol=7&client=js&version=4.3.1&flash=false`
      );

      ws.onopen = () => {
        console.log("WebSocket connection opened");
        // logger.info("WebSocket connection opened");
        // Reset reconnect attempts on successful connection
        reconnectAttempts = 0;

        // Send the correct Pusher subscription message format
        ws.send(
          JSON.stringify({
            event: "pusher:subscribe",
            data: {
              channel:
                import.meta.env.VITE_ENV_MODE == "DEV"
                  ? "interpreter-call"
                  : user.channel,
            },
          })
        );

        // Set up ping interval to keep connection alive (every 20 seconds)
        if (pingInterval) clearInterval(pingInterval);
        pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ event: "pusher:ping" }));
            console.log("Ping sent to keep WebSocket alive");
          }
        }, 20000); // 20 seconds interval to prevent 30-second timeout
      };

      ws.onmessage = (msg) => {
        const payload = JSON.parse(msg.data);
        console.log("WebSocket message:", payload);

        if (payload.event == "contact_center.engagement_user_answered") {
          setFormSubmitted(false);
          setCallEvent("ANSWERED");
          setCallInfo(parseCallEventPayload(payload.data));
        } else if (
          payload.event == "contact_center.engagement_wrap_up_ended" ||
          payload.event == "contact_center.engagement_ended"
        ) {
          setCallEvent("ENDED");
        } else if (payload.event === "pusher:pong") {
          console.log("Received pong from server");
        }
      };

      ws.onerror = (error) => {
        console.error("WebSocket error:", error);
      };

      ws.onclose = (event) => {
        console.log("WebSocket connection closed with code:", event.code);
        clearInterval(pingInterval);

        // Don't attempt to reconnect if we're unmounting (component cleanup)
        if (!event.wasClean && reconnectAttempts < maxReconnectAttempts) {
          // Calculate delay with exponential backoff (1s, 2s, 4s, 8s, etc.)
          const delay = baseReconnectDelay * Math.pow(2, reconnectAttempts);
          console.log(
            `Attempting to reconnect in ${delay}ms (attempt ${
              reconnectAttempts + 1
            }/${maxReconnectAttempts})`
          );

          reconnectTimeout = setTimeout(() => {
            reconnectAttempts++;
            setupWebSocket();
          }, delay);
        } else if (reconnectAttempts >= maxReconnectAttempts) {
          console.error(
            "Maximum reconnection attempts reached. Please refresh the page."
          );
        }
      };
    };

    // Initial WebSocket setup
    setupWebSocket();

    // Cleanup function when component unmounts
    return () => {
      console.log("Cleaning up WebSocket connection");
      if (ws && ws.readyState === WebSocket.OPEN) {
        // Set a flag or use a clean close code to indicate intentional closure
        ws.close(1000, "Component unmounting"); // 1000 is normal closure
      }
      if (pingInterval) clearInterval(pingInterval);
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
    };
  }, [user]);

  useEffect(() => {
    if (callEvent === "IDLE") {
      handleWSCallMissed();
    } else if (callEvent === "ENDED") {
      if (formSubmitted) {
        window.location.reload();
      }
    }
  }, [callEvent, formSubmitted]);

  const callViewProps = {
    user,
    callInfo,
    setFormSubmitted,
    setCallEvent,
    setCallInfo,
    callEvent,
  };

  if (import.meta.env.VITE_ENV_MODE == "DEV") {
    return (
      <CallView key={callInfo?.engagement_id} {...callViewProps} />
    );
  }
  // Only show CallLogs if we're in IDLE state or if the call has ENDED and the form has been submitted
  return callEvent == "IDLE" || (callEvent == "ENDED" && formSubmitted) ? (
    <CallLogs />
  ) : (
    <CallView key={callInfo?.engagement_id} {...callViewProps} />
  );
}
